import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button, Card, CardHeader, EmptyState, PageContainer } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import {
  getChatScenarios,
  startChatSimulation,
  submitChatResponse,
} from '../api/api';

export default function SocialEngineeringChat() {
  const { organizationId } = useParams();
  const { t } = useLanguage();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStep, result]);

  useEffect(() => {
    getChatScenarios(organizationId)
      .then(setScenarios)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [organizationId]);

  const handleStart = async (scenarioCode) => {
    setError('');
    setResult(null);
    setMessages([]);
    setChat(null);
    setCurrentStep(null);
    try {
      const data = await startChatSimulation(organizationId, scenarioCode);
      setChat({ attemptId: data.attemptId, scenarioCode: data.scenarioCode, attackerName: data.attackerName });
      setCurrentStep(data.firstStep);
      setMessages([{ type: 'attacker', text: data.firstStep.attackerMessage, stepIndex: data.firstStep.stepIndex }]);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectOption = async (opt) => {
    if (!chat || !currentStep || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      setMessages((prev) => [
        ...prev,
        { type: 'user', text: opt.text, optionIndex: opt.index },
      ]);

      const res = await submitChatResponse(
        organizationId,
        chat.attemptId,
        currentStep.stepIndex,
        opt.index
      );

      if (res.completed) {
        setResult(res);
        setCurrentStep(null);
      } else {
        setCurrentStep(res.nextStep);
        setMessages((prev) => [
          ...prev,
          { type: 'attacker', text: res.nextStep.attackerMessage, stepIndex: res.nextStep.stepIndex },
        ]);
      }
    } catch (err) {
      setError(err.message);
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  const riskBadge = {
    SAFE: { label: t.safeChoice, className: 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30' },
    HIGH: { label: t.highRisk, className: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30' },
  };
  const riskLevelLabel = {
    SAFE: t.safeChoice,
    HIGH: t.highRisk,
  };

  return (
    <Layout>
      <PageContainer>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 mb-2">
            {t.chatSimTitle}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {t.chatSimDesc}
          </p>

          {error && (
            <div className="bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-500/30 p-4 rounded-lg mb-6">{error}</div>
          )}

          {!chat ? (
            <Card className="overflow-hidden">
              <CardHeader title={t.selectScenario} subtitle={t.socialEngineeringIntroSubtitle || t.chatSimDesc} />
              <div className="p-6">
                {scenarios.length === 0 ? (
                  <EmptyState
                    title={t.noScenariosAvailable}
                    description={t.noScenariosDesc}
                  />
                ) : (
                  <div className="space-y-4">
                    {scenarios.map((s) => (
                      <div
                        key={s.code}
                        className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-cyan-500/50 hover:shadow-soft transition-all duration-200 bg-white dark:bg-slate-700/50"
                      >
                        <h3 className="font-medium text-slate-900 dark:text-slate-100">{s.title}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">{s.description}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">{t.attacker}: {s.attackerName}</p>
                        <Button onClick={() => handleStart(s.code)} className="mt-3">
                          {t.startConversation}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ) : result ? (
            <Card className="overflow-hidden">
              <CardHeader title={t.trainingResult} />
              <div className="p-6">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${
                    riskBadge[result.overallRiskLevel]?.className ?? 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-400 border border-slate-200 dark:border-slate-500/30'
                  }`}
                >
                  {riskBadge[result.overallRiskLevel]?.label ?? result.overallRiskLevel}
                </span>

                <p className="text-slate-700 dark:text-slate-300 mb-6">{result.trainingExplanation}</p>

                {result.responses?.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">{t.yourChoices}:</p>
                    <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                      {result.responses.map((r, i) => (
                        <li key={i}>
                          {r.stepIndex + 1}. &quot;{r.selectedOptionText}&quot; — {riskLevelLabel[r.riskLevel] || r.riskLevel}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button
                  onClick={() => {
                    setChat(null);
                    setResult(null);
                    setMessages([]);
                  }}
                >
                  {t.newConversation}
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="font-medium">{chat.attackerName}</span>
              </div>

              <div className="h-[520px] overflow-y-auto p-4 space-y-4 bg-[#F8FAFC] dark:bg-transparent"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm ${
                        m.type === 'attacker'
                          ? 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100'
                          : 'bg-[#06B6D4] dark:bg-cyan-500 text-white'
                      }`}
                    >
                      {m.type === 'attacker' && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 font-medium mb-1">{chat.attackerName}</p>
                      )}
                      <p className="text-sm">{m.text}</p>
                    </div>
                  </div>
                ))}

                {currentStep && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{t.howWouldYouRespond}</p>
                    <div className="space-y-2">
                      {currentStep.options?.map((opt) => (
                        <button
                          key={opt.index}
                          onClick={() => handleSelectOption(opt)}
                          disabled={submitting}
                          className="block w-full text-left px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-slate-300 dark:hover:border-cyan-500/50 hover:bg-slate-100 dark:hover:bg-cyan-500/10 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800"
                        >
                          {opt.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </Card>
          )}

          <div className="mt-6">
            <Link
              to={`/org/${organizationId}/simulations`}
              className="text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 font-medium"
            >
              ← {t.backToSimulations}
            </Link>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
}
